import React, { useState, useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { Loader } from 'screens/analytics/commonComponents';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import noDataGraphV1 from 'assets/assetsV1/illustrations/newIllustrations/noDataGraphV1.svg';

import { RATINGS_TYPES } from 'constants/ProjectConstants';
import { objectFlip } from 'constants/helper';
import { sendExpandedReviewChartEvent } from 'constants/mixpanelHelpers/genericEvents';
import theme from 'constants/theme';

import { getPerformanceReviewData } from 'services/analytics/review';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ExpandableContainerProvider } from 'uiToolkitV1/ExpandableContainerProvider';
import ReviewLineChart from 'uiToolkitV1/ReviewLineChart/ReviewLineChart';
import { TextHeader2, TextHeader3, TextLabel } from 'uiToolkitV1/Typography';

export const PerformanceReviewGraphPlaceholder = () => (
  <Box p={10} height="100%">
    <Box
      position="absolute"
      left="1rem"
      bottom="40%"
      style={{
        transform: 'rotate(-90deg)',
        transformOrigin: 'top left',
      }}
    >
      <TextLabel variant="body1">Rating →</TextLabel>
    </Box>
    <Box position="absolute" left="45%" bottom="1rem">
      <TextLabel variant="body1">Review Cycle →</TextLabel>
    </Box>
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="85%"
      borderLeft={`2px solid ${theme.palette.borders.lightGrey}`}
      borderBottom={`2px solid ${theme.palette.borders.lightGrey}`}
      mb={8}
    >
      <TextHeader2 color="text.g3" align="center" style={{ width: '60%' }}>
        Watch this space to know your growth from one review cycle to another
      </TextHeader2>
    </Box>
  </Box>
);

const calcYAxisTicks = ({ ratingMap = {} }) => Object.keys(ratingMap);

export const PerformanceReviewGraph = ({
  performanceReviewData,
  linesInfo,
  yAxisTicks,
  tooltipVersion = 'v0',
  yAxisLabel,
  tooltipProps = {},
  yAxisType,
  yAxisKey = 'numOverallRating',
  yAxisDomain,
}) => (
  <ReviewLineChart
    data={performanceReviewData.reviewRatingDetailsResponseList}
    linesInfo={linesInfo}
    xAxisKey="reviewCycle"
    yAxisKey={yAxisKey}
    xAxisLabel="Review Cycles →"
    yAxisLabel={yAxisLabel ?? 'Assessment →'}
    yAxisTicks={yAxisType === 'category' ? null : yAxisTicks}
    xAxisDomain={[0, 'auto']}
    yAxisDomain={yAxisDomain ?? [0, Object.keys(performanceReviewData.ratingMap).length]}
    xAxisTickFormatter={(x) => (x ? `${x.slice(0, 15)}...` : x)}
    yAxisTickFormatter={(value) => {
      // specifically for the use case where yAxisType is defined in props
      if (yAxisType === 'category') {
        return value;
      }
      if (yAxisType === 'number') {
        return Number.isNaN(Number(value)) ? value : Number(value);
      }

      // keeping rest of the logic as is for usages at other places, where it determines based on rating data
      return Number.isNaN(Number(performanceReviewData.ratingMap[value]))
        ? performanceReviewData.ratingMap[value]
        : Number(performanceReviewData.ratingMap[value]);
    }}
    xAxisType="category"
    yAxisType={yAxisType}
    yAxisType2={Number.isNaN(Number(Object.values(performanceReviewData.ratingMap)?.[0])) ? 'category' : ''}
    metaDataKey="ratingSplit"
    metaDataLabelMap={performanceReviewData.dataMap}
    tooltipVersion={tooltipVersion}
    tooltipProps={tooltipProps}
  />
);

const PerformanceReviewGraphV1 = ({
  isWithoutContainer = false,
  emptyMessage = '',
  isSidePanelGraph,
  empId = null,
}) => {
  const [performanceReviewData, setPerformanceReviewData] = useState({
    ratingMap: {},
    reviewRatingDetailsResponseList: [],
    dataMap: RATINGS_TYPES,
    color: '',
  });
  const { reviewRatingDetailsResponseList } = performanceReviewData ?? [];
  const [loading, setLoading] = useState(true);

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  useEffect(() => {
    const fetchPerformanceReviewData = () => {
      setLoading(true);
      getPerformanceReviewData({ empId: empId ?? loggedInEmployeeId })
        .then((res) => {
          const flippedRatingMap = objectFlip(res.ratingMap);
          const newReviewRatingDetailsResponseList = res.reviewRatingDetailsResponseList.map((response) => {
            const overallRating = Number.isNaN(Number(response.overallRating))
              ? response.overallRating
              : Number(response.overallRating);
            const mappedRating = res.ratingMap[overallRating];
            return {
              ...response,
              numOverallRating: mappedRating || overallRating,
            };
          });
          setPerformanceReviewData((prevPerformanceReviewData) => ({
            ...prevPerformanceReviewData,
            ratingMap: flippedRatingMap,
            reviewRatingDetailsResponseList: newReviewRatingDetailsResponseList,
            color: res.color,
          }));
        })
        .catch((err) => console.error('Error fetching data for performance review :', err))
        .finally(() => {
          setLoading(false);
        });
    };

    fetchPerformanceReviewData();
  }, [empId, loggedInEmployeeId]);

  const linesInfo = [
    {
      key: 'numOverallRating',
      name: 'Rating',
      color: '#4657AE',
    },
  ];

  const yAxisTicks = performanceReviewData?.ratingMap
    ? calcYAxisTicks({ ratingMap: performanceReviewData.ratingMap })
    : [];

  if (!reviewRatingDetailsResponseList?.length) {
    return (
      <Box mt="50px">
        <EmptyScreen illustrationSrc={noDataGraphV1} heading={emptyMessage} isSmallTextSize={isSidePanelGraph} />
      </Box>
    );
  }
  if (isWithoutContainer) {
    if (loading) {
      return <Loader />;
    }

    return performanceReviewData.reviewRatingDetailsResponseList.length !== 0 ? (
      <Box w="100%" h="300px">
        <PerformanceReviewGraph
          performanceReviewData={performanceReviewData}
          linesInfo={linesInfo}
          yAxisTicks={yAxisTicks}
        />
      </Box>
    ) : (
      <PerformanceReviewGraphPlaceholder />
    );
  }

  return (
    <ExpandableContainerProvider
      id="reviewPerformanceGraph"
      isParentDeterminesHeight
      onExpand={() => {
        sendExpandedReviewChartEvent({
          eventProperties: {
            source: 'review',
            type: 'Explore',
          },
        });
      }}
    >
      <Box height="100%">
        <TextHeader3 color="text.g1" bold ml={6} mt={6}>
          Performance outcome
        </TextHeader3>

        {loading && <Loader />}
        {!loading &&
          (performanceReviewData.reviewRatingDetailsResponseList.length !== 0 ? (
            <Box h="calc(100% - 44px)">
              <PerformanceReviewGraph
                performanceReviewData={performanceReviewData}
                linesInfo={linesInfo}
                yAxisTicks={yAxisTicks}
              />
            </Box>
          ) : (
            <PerformanceReviewGraphPlaceholder />
          ))}
      </Box>
    </ExpandableContainerProvider>
  );
};

export default PerformanceReviewGraphV1;
